import { useEffect, useRef, useState } from 'react';

// Per-row quick-action kebab menu for the /ar invoice table. A trailing "…"
// trigger toggles a small popover with "เปิดดู" (→ onOpen), "รับชำระ" (→ onReceive,
// when the row is still open), and "ยกเลิก" (→ onVoid, only for a posted,
// untouched invoice). Closes on outside mousedown, mirroring the SearchSelect
// outside-close pattern. Plain prototype.css (btn ghost) only.

export interface RowActionsProps {
  canReceive: boolean;
  // Voiding is reserved for a posted, untouched invoice (no payments, no credit
  // notes). When false the menu item is hidden — the AR table computes the gate.
  canVoid?: boolean;
  onOpen: () => void;
  onReceive: () => void;
  onVoid?: () => void;
}

export function RowActions({ canReceive, canVoid, onOpen, onReceive, onVoid }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function run(action: () => void) {
    setOpen(false);
    action();
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="btn ghost"
        style={{ padding: '2px 8px', fontSize: 16, lineHeight: 1 }}
        aria-label="เมนู"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        …
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 120,
            background: '#fff',
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            zIndex: 200,
            padding: 4,
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="btn ghost"
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px' }}
            onClick={() => run(onOpen)}
          >
            เปิดดู
          </button>
          {canReceive && (
            <button
              type="button"
              role="menuitem"
              className="btn ghost"
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px' }}
              onClick={() => run(onReceive)}
            >
              รับชำระ
            </button>
          )}
          {canVoid && onVoid && (
            <button
              type="button"
              role="menuitem"
              className="btn ghost"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '7px 10px',
                color: 'var(--negative)',
              }}
              onClick={() => run(onVoid)}
            >
              ยกเลิก
            </button>
          )}
        </div>
      )}
    </div>
  );
}
